import Icon from '@mdi/react'
import { Collapse, Button, Form, Divider } from 'antd'
import { MultipleRenderSchema, RenderField, RenderSchema } from '../../utils/FormRenderer'
import {
	mdiTextBoxCheckOutline,
	mdiOpenInNew,
	mdiExclamationThick,
	mdiChevronRight,
	mdiInformationOutline,
	mdiSigma,
	mdiVectorLink,
	mdiVectorUnion,
	mdiFileCloudOutline,
	mdiExclamation,
} from '@mdi/js'
import { SegmentsEnum } from '../../utils/Enums'
import {
	BiltyTypeSchema,
	InitialKantaSchema,
	BiltyDetailsSchema,
	DriverDetailsSchema,
	InitialBiltyDetailsSchema,
	TrackerAvailabilitySchema,
	CreationAdditionalDetailsSchema,
	PostCreationAdditionalDetailsSchema,
	FinalKantaSchema,
} from '../../utils/FormSchemas'
import { printGeneratedBilty, combineChildOrders, biltyObjectFunction } from '../../utils/Functions'
import { useEffect } from 'react'
import { Tooltip } from '@material-ui/core'

const { Panel } = Collapse

const updateComponentState = (index, newValue, setComponenetState) => {
	setComponenetState((prevStates) => {
		const updatedStates = [...prevStates]
		updatedStates[index] = newValue
		return updatedStates
	})
}

/* ============================================================================================== */
/* =============================== CREATE BILTY MODAL COMPONENTS ================================ */
/* ============================================================================================== */

export const ConfigureOrderDetails = ({ form, formWatch, data, segment, modalRefState }) => {
	const documentOrdersMapData = data?.record || {}
	const movementType = Object.values(documentOrdersMapData)?.flat()?.[0]?.movementType
	const partialSubOrderExists = Object.values(documentOrdersMapData)
		?.flat()
		?.some((order) => order?.totalCount)
	const movedUnitsExist = Object.values(documentOrdersMapData)
		?.flat()
		?.find((order) => order?.movedUnits)?.movedUnits

	useEffect(() => {
		modalRefState.current = data?.record
		if (movementType === 'ImportShifting' && partialSubOrderExists) {
			form.setFieldsValue({
				movedUnits:
					Object.values(documentOrdersMapData)
						?.flat()
						?.find((order) => order?.movedUnits)?.movedUnits || 'Units',
			})
		}
	}, [documentOrdersMapData, movementType, partialSubOrderExists]) // eslint-disable-line
	return (
		<div className='flex flex-col p-6 h-full w-full overflow-y-auto content-center'>
			{movementType === 'ImportShifting' && (
				<div className='flex flex-col w-full bg-white rounded p-4 shadow-[0_0_4px_0_rgba(0,0,0,0.18)]'>
					<div className='font-normal text-sm'>Select Movement Metric</div>
					<div className='!h-8 mt-1'>
						{RenderField({
							type: 'radioGroup',
							name: 'movedUnits',
							dataObject: {
								Units: { key: 'Units', long: 'Units' },
								Kilos: { key: 'Kilos', long: 'Weight' },
							},
							disabled: movedUnitsExist,
							initialValue: 'Units',
							layout: 'horizontal',
						})}
					</div>
				</div>
			)}
			<div className='font-semibold text-base text-secondary-200 my-4'>
				Selected {segment === 'Import' ? 'BLs' : segment === 'Export' ? 'CROs' : 'Orders'}
			</div>
			{Object?.keys(documentOrdersMapData)?.length > 0 &&
				Object?.keys(documentOrdersMapData)?.map((document, index) => {
					return (
						<div className={`flex flex-col bg-white rounded shadow-[0_0_4px_0_rgba(0,0,0,0.18)] ${index !== 0 ? 'mt-4' : ''}`}>
							{segment !== 'LongHaul' && (
								<div className='flex items-center pl-3 font-semibold text-base text-primary-800 h-12'>
									{SegmentsEnum[segment]?.documentTypeShort} {document}
								</div>
							)}
							<div className={`flex flex-col ${segment !== 'LongHaul' ? 'border-t-2 border-gray-100 rounded-b' : 'rounded'} !bg-white `}>
								{documentOrdersMapData?.[document]?.map((order, index) => {
									const partialMatched = order?.totalCount
									const remainingUnits = order?.totalCount - order?.movedCount
									const blContainerUnits = order?.numberOfUnits

									return (
										<div
											key={order?._id}
											className={`flex flex-row justify-between items-center ${index < documentOrdersMapData?.[document]?.length - 1 ? 'border-b-2 border-gray-100' : ''}`}
										>
											<div className={`h-full w-[40%] ${order?.movementType === 'ImportShifting' && 'border-r-2 border-gray-100'}`}>
												<div className='flex flex-col h-full justify-center py-4 px-4'>
													<div className='font-semibold text-base'>
														{SegmentsEnum[segment]?.initial} - {order?.subOrderNumber}
													</div>
													{order?.containerNumber && <div className='text-sm font-normal text-black-300 mt-1'>Container# {order?.containerNumber}</div>}
												</div>
											</div>
											{order?.movementType === 'ImportShifting' && (
												<div className='h-full w-full bg-grey-500 rounded-br'>
													<div className='flex flex-row justify-center items-end pt-2 pb-5 px-4'>
														<div className='!mr-4 w-[45%] !my-auto !enable-pointer'>
															{RenderField({
																minValue: 1,
																type: 'number',
																required: true,
																itemClassName: '!mb-0',
																addonAfter: formWatch?.movedUnits,
																name: ['partialMatch', order?._id, 'movedCount'],
																label: `Moved ${formWatch?.movedUnits === 'Units' ? 'Units' : 'Weight'}`,
																placeholder: `Enter Moved ${formWatch?.movedUnits === 'Units' ? 'Units' : 'Weight'}`,
																maxValue: partialMatched ? remainingUnits : parseInt(form?.getFieldValue(['partialMatch', order?._id, 'totalCount'])),
															})}
														</div>
														{!order?.totalCount ? (
															<div className='flex flex-row items-center w-[45%] !my-auto !mb-0 !enable-pointer'>
																{RenderField({
																	type: 'number',
																	required: true,
																	itemClassName: '!mb-0',
																	addonAfter: formWatch?.movedUnits,
																	name: ['partialMatch', order?._id, 'totalCount'],
																	label: `Total ${formWatch?.movedUnits === 'Units' ? 'Units' : 'Weight'}`,
																	placeholder: `Enter Total ${formWatch?.movedUnits === 'Units' ? 'Units' : 'Weight'}`,
																	minValue: parseInt(form?.getFieldValue(['partialMatch', order?._id, 'movedCount'])),
																	initialValue: blContainerUnits,
																})}
															</div>
														) : (
															<div className='flex flex-row items-center border justify-center border-dotted rounded border-primary-800 w-[45%] py-4 px-2 h-[48px] !mb-0'>
																<Icon path={mdiInformationOutline} size={0.9} className='mr-2' color='#676767' />
																<div className='text-primary-800 font-bold text-sm mr-2'>
																	{order?.totalCount - (order?.movedCount + parseInt(form?.getFieldValue(['partialMatch', order?._id, 'movedCount']) || 0))}
																</div>
																<div className='font-normal text-sm'>{formWatch?.movedUnits} Remain</div>
															</div>
														)}
														{!!blContainerUnits && (
															<Tooltip
																title='The pre-filled value is the total units in the container as mentioned on the BL. You may change it if required.'
																placement='top'
																color='#676767'
															>
																<Icon path={mdiInformationOutline} size={1.1} className='mb-3 ml-2' color='#676767' />
															</Tooltip>
														)}
													</div>
												</div>
											)}
										</div>
									)
								})}
							</div>
						</div>
					)
				})}
		</div>
	)
}

export const DetailsColumn = ({ data: { record }, form, modalType, segment, expandedKeys, setExpandedKeys }) => {
	const biltyOrderConnectionObject = {}
	const formMovedUnits = form.getFieldValue('movedUnits')
	const formPartialMatchedValues = form.getFieldValue('partialMatch')

	record?.bilty?.forEach(
		(bilty) =>
			(biltyOrderConnectionObject[bilty?.biltyNumber || undefined] = [
				...(biltyOrderConnectionObject[bilty?.biltyNumber] || []),
				...record?.subOrders?.filter((order) => (segment === 'Import' ? order?.documentNumber === bilty?.documentNumber : true)),
			])
	)

	const documentOrdersMapData = modalType === 'createBilty' ? record : biltyOrderConnectionObject

	const formMovedCount = Object.values(formPartialMatchedValues || {})?.reduce((acc, curr) => acc + parseInt(curr?.movedCount || 0), 0)
	const jobMovedCount = Object.values(documentOrdersMapData || {})
		?.flat()
		?.reduce((acc, curr) => acc + parseInt(curr?.movementCount || 0), 0)
	const jobMovedUnits = Object.values(documentOrdersMapData || {})
		?.flat()
		?.find((order) => order?.movedUnits)?.movedUnits
	const partialSubOrderExists = Object.values(documentOrdersMapData || {})
		?.flat()
		?.some((order) => order?.totalCount)

	return (
		<div className='flex flex-col justify-between px-4 py-6 h-full w-full overflow-y-auto bg-white shadow-[0_0_4px_0_rgba(0,0,0,0.18)] content-center'>
			<div>
				<div className='font-semibold text-black-200 mb-3'>{`Selected ${
					modalType === 'createBilty' ? `${segment === 'LongHaul' ? 'Orders' : 'Containers'}` : 'Bilties'
				} `}</div>
				{Object?.keys(documentOrdersMapData)?.map((document, index) => {
					return (
						<>
							{segment !== 'LongHaul' ? (
								<>
									<Collapse
										size='small'
										bordered={false}
										expandIconPosition='end'
										className='border-y-2 border-gray-100 !bg-white'
										destroyInactivePanel={true}
										defaultActiveKey={document}
										onChange={(keyArray) => updateComponentState(index, keyArray, setExpandedKeys)}
									>
										<Panel
											key={document}
											showArrow={false}
											forceRender={true}
											header={
												<div className='flex flex-row justify-start'>
													<div className='font-semibold leading-4 text-base text-primary-800 py-2 mr-4'>
														{modalType !== 'createBilty' ? '' : SegmentsEnum[segment]?.documentTypeShort} {document}
													</div>
													<div className={`self-center`}>
														<Icon
															className={` transition-all !duration-500 ${expandedKeys?.[index]?.includes(document) ? 'rotate-90' : 'rotate-0'}`}
															path={mdiChevronRight}
															size={1}
														/>
													</div>
												</div>
											}
										>
											{documentOrdersMapData[document]?.map((order) => {
												return (
													<div className='flex flex-col justify-center items-start py-3'>
														<div className='font-semibold text-base leading-5 mb-1.5'>
															{SegmentsEnum[segment]?.initial}-{order?.subOrderNumber}
														</div>
														<div className='font-normal text-base leading-5 text-black-300 mb-1.5'>Container# {order?.containerNumber || '---'}</div>
														{(order?.partialMatched || order?.completeMatched || formPartialMatchedValues?.[order?._id]) && (
															<div className='flex flex-row  items-center py-1 px-2 rounded bg-[#F5F5F5]'>
																<span className='mr-2'>
																	<Icon
																		path={
																			order?.partialMatched ||
																			(formPartialMatchedValues?.[order?._id] &&
																				(order?.totalCount
																					? parseInt(formPartialMatchedValues?.[order?._id]?.movedCount) !== order?.totalCount
																					: parseInt(formPartialMatchedValues?.[order?._id]?.movedCount) !== parseInt(formPartialMatchedValues?.[order?._id]?.totalCount)))
																				? mdiVectorLink
																				: mdiVectorUnion
																		}
																		size={0.8}
																		color='#676767'
																	/>
																</span>
																<span className='mr-2 text-xs font-bold text-black-400'>
																	{order?.movedUnits === 'Kilos' || formMovedUnits === 'Kilos' ? 'Weight Moved :' : 'Moved Units :'}
																</span>
																<span className='text-black-300 text-xs font-bold'>{`${
																	formPartialMatchedValues?.[order?._id] ? parseInt(formPartialMatchedValues[order?._id]?.movedCount) : order?.movementCount
																} ${order?.movedUnits === 'Kilos' || formMovedUnits === 'Kilos' ? 'Kilos' : ''}`}</span>
															</div>
														)}
													</div>
												)
											})}
										</Panel>
									</Collapse>
								</>
							) : (
								<div>
									{documentOrdersMapData[document]?.map((order) => {
										return (
											<div className='flex flex-col justify-center items-start py-3'>
												<div className='font-semibold text-base leading-5 mb-1.5'>
													{SegmentsEnum[segment]?.initial}-{order?.subOrderNumber}
												</div>
												<div className='font-normal text-base leading-5 text-black-300 mb-1.5'>Container# {order?.containerNumber || '---'}</div>
											</div>
										)
									})}
								</div>
							)}
							{index < Object?.keys(documentOrdersMapData)?.length - 1 && <div className='w-full border-[0.5px] border-[rgba(0, 0, 0, 0.12)] my-3'></div>}
						</>
					)
				})}
			</div>
			{(partialSubOrderExists || formPartialMatchedValues) && (
				<div className='flex flex-row items-center justify-center py-3 px-2 mx-3 mt-3 border border-dashed border-gray-200 rounded bg-green-100'>
					<span className='mr-1.5'>
						<Icon path={mdiSigma} size={0.8} color='#467D48' />
					</span>
					<span className='mr-1.5 text-xs font-semibold text-green-300'>
						{jobMovedUnits === 'Kilos' || formMovedUnits === 'Kilos' ? 'Total Weight :' : 'Total Moved Units :'}
					</span>
					<span className='text-green-300 text-xs font-semibold'>{`${formMovedCount || jobMovedCount} ${
						jobMovedUnits === 'Kilos' || formMovedUnits === 'Kilos' ? 'Kilos' : ''
					}`}</span>
				</div>
			)}
		</div>
	)
}

export const MultipleBiltyInformationSchema = ({
	record,
	modalType,
	segment,
	showDivider,
	setPromiseLoading,
	SchemaToRender,
	label,
	required = false,
}) => {
	return (
		<>
			<div className='text-base font-medium text-black-300'>{label}</div>
			<div className='w-full bg-white rounded border border-black-200 px-4 py-5 mt-3'>
				{Object.keys(record)?.map((document, index) => {
					return (
						<div className='flex flex-col'>
							{segment !== 'LongHaul' && (
								<div className='font-semibold mb-2'>
									{modalType !== 'createBilty' ? '' : SegmentsEnum[segment]?.documentTypeShort} {document}
								</div>
							)}
							{MultipleRenderSchema(
								SchemaToRender({ segment, uploadID: record?.[document]?.[0]?._id || record?.[document]?._id, document, required, setLoading: setPromiseLoading })
							)}
							{index < Object?.keys(record)?.length - 1 && <div className='w-full border-[0.5px] border-dashed border-[rgba(0, 0, 0, 0.12)] mt-1 mb-4'></div>}
						</div>
					)
				})}
			</div>
			{showDivider !== false && <Divider />}
		</>
	)
}

export const ManualBiltyTypeSchema = ({ record, modalType, segment, setPromiseLoading }) => {
	return (
		<>
			<div>{RenderSchema(BiltyTypeSchema())}</div>
			<Form.Item key={'biltyTypeDependency'} shouldUpdate={(prevValues, currentValues) => prevValues['biltyType'] !== currentValues['biltyType']} noStyle>
				{({ getFieldValue }) =>
					!['systemGenerated'].includes(getFieldValue('biltyType')) &&
					MultipleBiltyInformationSchema({
						record,
						modalType,
						segment,
						setPromiseLoading,
						SchemaToRender: InitialBiltyDetailsSchema,
						label: 'Manual Bilty Information',
					})
				}
			</Form.Item>
		</>
	)
}

export const CreateBiltyInformation = ({ data: { record }, segment, modalType, trackers, setPromiseLoading, isLot }) => {
	const mappedRecord = Object?.values(record)?.flat()
	return (
		<div className='flex flex-col p-6 h-full w-full overflow-y-auto content-center bg-secondaryBackground'>
			<div>{ManualBiltyTypeSchema({ record, modalType, segment, setPromiseLoading })}</div>
			<div>{MultipleRenderSchema(DriverDetailsSchema({ uploadID: mappedRecord?.[0]?._id, setLoading: setPromiseLoading, isLot }))}</div>
			<div>{MultipleRenderSchema(TrackerAvailabilitySchema({ trackers }))}</div>
			<div>
				{MultipleBiltyInformationSchema({
					record,
					modalType,
					segment,
					setPromiseLoading,
					SchemaToRender: InitialKantaSchema,
					label: 'Initial Kanta Information',
				})}
			</div>
			<div>{MultipleRenderSchema(CreationAdditionalDetailsSchema({ segment, uploadID: mappedRecord?.[0]?._id, setLoading: setPromiseLoading }))}</div>
		</div>
	)
}

export const PostCreationBiltyInformation = ({ data: { record }, segment, setPromiseLoading }) => {
	const biltyDataObject = biltyObjectFunction(record?.bilty)

	return (
		<div className='flex flex-col p-6 h-full w-full overflow-y-auto content-center bg-secondaryBackground'>
			<div>
				{MultipleBiltyInformationSchema({ segment, record: biltyDataObject, setPromiseLoading, label: 'Bilty Details', SchemaToRender: BiltyDetailsSchema })}
			</div>
			<div>
				{MultipleBiltyInformationSchema({
					segment,
					setPromiseLoading,
					record: biltyDataObject,
					label: 'Final Kanta Information',
					SchemaToRender: FinalKantaSchema,
				})}
			</div>
			<div>{MultipleRenderSchema(PostCreationAdditionalDetailsSchema({ segment, uploadID: record?._id, setLoading: setPromiseLoading }))}</div>
		</div>
	)
}

export const BiltyCreationResponse = ({
	form,
	promiseLoading,
	modalRefState,
	setPromiseLoading,
	segment,
	cancel,
	modalType,
	showResultPage: { result, values },
}) => {
	const error = result?.type === 'error'
	const Job = result?.response?.Job

	const formPartialMatchedValues = form.getFieldValue('partialMatch')
	return (
		<div className='flex flex-col items-center justify-center rounded-lg bg-white px-16 py-20'>
			<div className={`flex justify-center items-center h-40 w-40 ${error ? 'bg-red-300' : 'bg-[#E4F7E5]'} rounded-full mb-6`}>
				<Icon path={error ? mdiExclamationThick : mdiTextBoxCheckOutline} size={2.6} color={error ? '#c63d3d' : '#467D48'} />
			</div>
			<div className='text-center font-bold text-lg mb-6'>
				{error ? 'Bilty Creation Failed' : modalType === 'printBilty' ? 'Click below to print bilty' : 'Bilty has been created successfully'}
			</div>
			{error && <div className='font-normal text-base'>Bilty creation has failed. Kindly reach out to the BridgeLinx product team for assistance.</div>}
			{!error &&
				Job?.bilty?.map((bilty) => {
					const biltyGenerationData = combineChildOrders({
						orders: modalRefState?.current?.[bilty?.documentNumber || Object.keys(modalRefState?.current)?.[0]],
						values,
					})
					return (
						<div className='w-full bg-secondaryBackground border border-dashed border-primary-800 rounded-lg mb-6 py-4 px-6'>
							<div className='flex flex-row h-full justify-between'>
								<div className='flex flex-col justify-center'>
									<div className='text-start text-base font-bold text-primary-800 mb-1'>{bilty?.biltyNumber}</div>
									{segment === 'Import' && <div className=' text-start text-base font-normal mb-1'>BL # {bilty?.documentNumber}</div>}
									<div className='flex flex-row flex-wrap'>
										{segment === 'Import' &&
											modalRefState?.current[bilty?.documentNumber || Object.keys(modalRefState?.current)?.[0]]?.map((subOrder) => (
												<div className='flex flex-row mr-2 items-center bg-white px-4 py-1 rounded-3xl border border-[rgba(0, 0, 0, 0.12)]'>
													<span className='text-sm font-normal text-black-300'>Container # {subOrder?.containerNumber}</span>{' '}
													{formPartialMatchedValues?.[subOrder?._id] &&
														(subOrder?.totalCount
															? parseInt(formPartialMatchedValues?.[subOrder?._id]?.movedCount) !== subOrder?.totalCount
															: parseInt(formPartialMatchedValues?.[subOrder?._id]?.movedCount) !== parseInt(formPartialMatchedValues?.[subOrder?._id]?.totalCount)) && (
															<span>
																<Icon className='ml-2' path={mdiVectorLink} size={0.8} color='#1C75BC' />
															</span>
														)}
												</div>
											))}
									</div>
								</div>
								{values?.biltyType === 'systemGenerated' && (
									<div
										className='flex justify-center items-center mr-3 cursor-pointer'
										onClick={() =>
											printGeneratedBilty({
												values: {
													...values,
													driverName: Job?.Driver?.name,
													driverPhone: Job?.Driver?.phoneNumber,
													driverCNIC: Job?.Driver?.cnic,
													driverVehicleRegistration: Job?.Vehicle?.registrationNumber,
													trackerName: Job?.Tracker?.name,
												},
												record: biltyGenerationData,
												bilty,
												setLoading: setPromiseLoading,
											})
										}
									>
										<Tooltip title={<div className='!p-0.5 w-fit text-xs font-semibold'>Print Bilty</div>} placement='top' color='#676767'>
											<Icon path={mdiOpenInNew} size={1.4} color='#1C75BC' />
										</Tooltip>
									</div>
								)}
							</div>
						</div>
					)
				})}
			<div className='mt-10'>
				<Button className='btn-secondary' onClick={() => cancel()} loading={promiseLoading}>
					GO BACK TO LISTING
				</Button>
			</div>
		</div>
	)
}

export const PrintBiltyModal = ({ form, data: { record }, promiseLoading, setPromiseLoading, segment, modalType, cancel }) => {
	return (
		<div className='flex flex-col items-center justify-center rounded-lg bg-white px-16 py-20'>
			<div className='flex justify-center items-center h-40 w-40 bg-[#E4F7E5] rounded-full mb-6'>
				<Icon path={mdiTextBoxCheckOutline} size={2.6} color={'#467D48'} />
			</div>
			<div className='text-center font-bold text-lg mb-6'>Click below to print bilty</div>
			{record?.bilty?.map((bilty) => {
				const biltySubOrders = record?.subOrders?.filter((order) => (segment === 'Import' ? order?.documentNumber === bilty?.documentNumber : true))
				const biltyGenerationData = combineChildOrders({ orders: biltySubOrders })
				return (
					<div className='w-full bg-secondaryBackground border border-dashed border-primary-800 rounded-lg mb-6 py-4 px-6'>
						<div className='flex flex-row h-full justify-between'>
							<div className='flex flex-col justify-center'>
								<div className='text-start text-base font-bold text-primary-800 mb-1'>{bilty?.biltyNumber}</div>
								{segment === 'Import' && <div className=' text-start text-base font-normal mb-1'>BL # {bilty?.documentNumber}</div>}
								<div className='flex flex-row flex-wrap'>
									{segment === 'Import' &&
										biltySubOrders?.map((subOrder) => (
											<div className='flex flex-row mr-2 items-center bg-white px-4 py-1 rounded-3xl border border-[rgba(0, 0, 0, 0.12)]'>
												<span className='text-sm font-normal text-black-300'>Container # {subOrder?.containerNumber}</span>{' '}
												{subOrder?.partialMatched && (
													<span>
														<Icon className='ml-2' path={mdiVectorLink} size={0.8} color='#1C75BC' />
													</span>
												)}
											</div>
										))}
								</div>
							</div>
							{bilty?.biltyType === 'systemGenerated' && (
								<div
									className='flex justify-center items-center mr-3'
									onClick={() =>
										printGeneratedBilty({
											record: {
												...biltyGenerationData,
												remarks: bilty?.remarks,
												biltyNumber: bilty?.biltyNumber,
												driverName: record?.Driver?.name,
												driverCNIC: record?.Driver?.cnic,
												trackerName: record?.Tracker?.name,
												driverPhone: record?.Driver?.phoneNumber,
												driverVehicleRegistration: record?.Vehicle?.registrationNumber.toLowerCase(),
											},
											bilty,
											setLoading: setPromiseLoading,
										})
									}
								>
									<Tooltip title={<div className='!p-0.5 w-fit text-xs font-semibold'>Print Bilty</div>} placement='top' color='#676767'>
										<Icon className='cursor-pointer' path={mdiOpenInNew} size={1.4} color='#1C75BC' />
									</Tooltip>
								</div>
							)}
						</div>
					</div>
				)
			})}
			<div className='mt-10'>
				<Button className='btn-secondary' onClick={() => cancel()} loading={promiseLoading}>
					GO BACK TO LISTING
				</Button>
			</div>
		</div>
	)
}

export const SaveBiltyModal = ({ data: { record } }) => {
	const bilties = record?.bilty

	return (
		<div className='flex flex-col justify-center items-center p-6 h-full w-full overflow-y-auto content-center bg-secondaryBackground'>
			<div className='flex flex-col items-center bg-white rounded-lg px-4 py-4 w-full h-full'>
				<div className='flex justify-center items-center h-24 w-24 bg-[#E4F7E5] rounded-full mb-6'>
					<Icon path={mdiFileCloudOutline} size={2} color={'#467D48'} />
				</div>
				<div className='text-sm font-medium text-black-300 mb-3 text-center'>
					Are you sure you want to generate & save bilty document for the following{' '}
					{bilties?.length > 1 ? `bilty numbers ${bilties?.map((bilty) => bilty?.biltyNumber)?.join(', ')}` : `bilty number${bilties?.[0]?.biltyNumber} bilty`}?
				</div>
			</div>
		</div>
	)
}

export const CancelBilty = ({ data: { record } }) => {
	const bilties = record?.bilty
	return (
		<div className='flex flex-col p-6 h-full w-full overflow-y-auto content-center bg-secondaryBackground'>
			<div className='text-sm font-medium text-black-300 mb-3'>
				Are you sure you want to cancel the following{' '}
				{bilties?.length > 1 ? `${bilties?.map((bilty) => bilty?.biltyNumber)?.join(', ')} bilties` : `${bilties?.[0]?.biltyNumber} bilty`}?
			</div>
			<div>
				{RenderField({
					type: 'textArea',
					name: 'cancelReason',
					required: true,
					label: 'Reason for cancellation',
					message: 'Reason for cancellation is required.',
				})}
			</div>
		</div>
	)
}

export const AddMissingKanta = ({ data: { record, selectedBiltyNumber }, modalType, setPromiseLoading, segment }) => {
	const biltyDataObject = biltyObjectFunction(
		record?.bilty?.filter((bilty) => (selectedBiltyNumber ? bilty?.biltyNumber === selectedBiltyNumber : true))
	)

	return (
		<div className='flex flex-col p-6 h-full w-full overflow-y-auto content-center bg-secondaryBackground'>
			<div>
				{MultipleBiltyInformationSchema({
					record: biltyDataObject,
					modalType,
					segment,
					setPromiseLoading,
					showDivider: false,
					required: true,
					SchemaToRender: InitialKantaSchema,
					label: 'Initial Kanta Information',
				})}
			</div>
		</div>
	)
}

export const MarkOrderComplete = ({ data: { record } }) => {
	const subOrder = Object.values(record)?.flat()?.[0]

	return (
		<div className='flex flex-col justify-center items-center p-6 h-full w-full overflow-y-auto content-center bg-secondaryBackground'>
			<div className='flex flex-col items-center w-full h-full bg-white rounded-lg px-4 py-4'>
				<div className='flex justify-center items-center h-24 w-24 bg-red-300 rounded-full mb-6'>
					<Icon path={mdiExclamation} size={2} color={'#c63d3d'} />
				</div>
				<div className='text-sm font-medium text-black-300 text-center'>
					Are you sure that you want to complete the order <span className='text-primary-800 font-semibold'>{subOrder?.subOrderNumber}</span> with{' '}
					<span className='text-primary-800 font-semibold'>
						{subOrder?.totalCount - subOrder?.movedCount} {subOrder?.movedUnits === 'Kilos' ? 'kilos' : 'units'}
					</span>{' '}
					remaining.
				</div>
				<div className='text-sm font-medium text-black-300 mb-3 text-center'>
					The following action is irreversible. Please confirm that you want to complete the order.
				</div>
			</div>
		</div>
	)
}
